import {
  ActionIcon,
  Box,
  Group,
  Menu,
  NumberInput,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { IconChevronDown, IconTrash, IconX } from '@tabler/icons-react';
import React, { Fragment, useState } from 'react';
import { ViewField } from 'src/non-financial-data/components/fields/ViewFormField';
import { useFamilyMembers } from 'src/non-financial-data/v2/family/useFamilyMembers';
import { Button } from 'src/design-system/Button';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type FamilyMember = ReturnType<typeof useFamilyMembers>['members'][number];

export type OwnerRowValue = {
  id?: string | null;
  client_id?: string | null;
  external_name?: string | null;
  ownership_percentage?: number | null;
};

const EXTERNAL = 'external';

// Products whose backend persists an owners list. The owner editor must only run for
// these; other products (crypto, collectibles, pensions) have no owner tables, so
// collecting owners for them would silently drop them on save.
const OWNER_SUPPORTED_PRODUCTS = new Set([
  'Property',
  'InvestmentAndCash',
  'Mortgage',
  'Loan',
  'SpendingAccount',
  'Protection',
]);

export const productSupportsOwners = (product: string): boolean =>
  OWNER_SUPPORTED_PRODUCTS.has(product);

/** A share is captured to the nearest hundredth of a percent, both typed and derived from money. */
const SHARE_DECIMAL_PLACES = 2;

/** The owners' shares, to the same hundredth of a percent each one is captured at. */
function ownersShareSum(owners: OwnerRowValue[]): number {
  const sum = owners.reduce((acc, owner) => acc + (Number(owner.ownership_percentage) || 0), 0);
  return Math.round(sum * 100) / 100;
}

// Some splits cannot be written exactly in hundredths — three equal owners come to 33.33% each,
// a hundredth short of the whole — so the last hundredth is forgiven rather than making the
// adviser pick one owner to carry the remainder. Compared in hundredths, since 99.99 and 100 are
// a shade further apart in binary than the hundredth they differ by.
export function ownersSumTo100(owners: OwnerRowValue[]): boolean {
  return Math.abs(Math.round(ownersShareSum(owners) * 100) - 10000) <= 1;
}

export const isExternalOwner = (owner: OwnerRowValue): boolean => !owner.client_id;

/** 'valuation' is the reworked financial form's table; 'classic' is the original inline list. */
type Variant = 'classic' | 'valuation';

const CLASSIC_NAME_WIDTH = 200;
const VALUATION_NAME_WIDTH = '50%';
const PERCENTAGE_WIDTH = 90;
const AMOUNT_WIDTH = 140;
const CLASSIC_AMOUNT_WIDTH = 110;
const REMOVE_WIDTH = 32;

/** A figure the adviser reads off rather than types into, in the weight read mode uses. */
function ReadOnlyCell({
  width,
  muted = false,
  children,
}: {
  width: number;
  muted?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Text size="sm" w={width} ta="right" c="gray.10" fw={muted ? undefined : FONT_WEIGHTS.semibold}>
      {children}
    </Text>
  );
}

type Props = {
  owners: OwnerRowValue[];
  onChange: (next: OwnerRowValue[]) => void;
  members: FamilyMember[];
  /** The item's total value, used for the per-owner money readout. */
  total?: number;
  /**
   * Sets the item's total value, when naming an owner's slice in money is allowed to say what
   * the whole is worth. Without it the money column is read-only: a surface that cannot move
   * the total — the Data Upload review card, reading a detection's own figure — has nothing to
   * record such an edit against.
   */
  onTotalChange?: (total: number) => void;
  /**
   * Renders an owner's slice of `total`. Supplied by the caller so each surface keeps its own
   * money convention: the create form uses the tenant's currency, the Data Upload Review card
   * uses the currency the detection itself carries, matching the value shown above it.
   */
  formatAmount: (amount: number) => string;
  /** Create form shows a joint toggle that reveals the rows; review always shows them. */
  showJointToggle?: boolean;
  isJoint?: boolean;
  onJointChange?: (joint: boolean) => void;
  /** Locks the rows once the surface has saved them and further edits would go nowhere. */
  disabled?: boolean;
  /**
   * A surface that is being read rather than edited. Unlike `disabled`, which leaves the
   * controls on screen greyed out, this prints the shares as text — Mantine renders a disabled
   * input at 0.6 opacity in its muted colour, which is barely legible, and the fields around
   * this table read as plain text in the same mode.
   */
  readOnly?: boolean;
  variant?: Variant;
};

// Controlled owners editor shared by the create form (via OwnerSharesSection) and the
// Data Upload Review card. Owns no form/route state — it renders the given owners and
// reports edits through onChange.
export function OwnersEditor({
  owners,
  onChange,
  members,
  total = 0,
  formatAmount,
  onTotalChange,
  showJointToggle = false,
  isJoint = false,
  onJointChange,
  disabled = false,
  readOnly = false,
  variant = 'classic',
}: Props) {
  const usedClientIds = new Set(owners.map(owner => owner.client_id).filter(Boolean));
  const availableMembers = members.filter(member => !usedClientIds.has(member.clientId));
  const isValuation = variant === 'valuation';
  const nameWidth = isValuation ? VALUATION_NAME_WIDTH : CLASSIC_NAME_WIDTH;

  const memberName = (clientId: string) => {
    const member = members.find(candidate => candidate.clientId === clientId);
    return member ? `${member.client.firstName} ${member.client.lastName}` : 'Unknown owner';
  };

  const addOwner = (value: string) =>
    onChange(
      value === EXTERNAL
        ? [...owners, { external_name: '', ownership_percentage: null }]
        : [...owners, { client_id: value, ownership_percentage: null }],
    );

  const removeOwner = (index: number) => onChange(owners.filter((_, i) => i !== index));

  const updateOwner = (index: number, patch: Partial<OwnerRowValue>) =>
    onChange(owners.map((owner, i) => (i === index ? { ...owner, ...patch } : owner)));

  // A share says what part of the item an owner holds, so naming that part in money says what
  // the whole item is worth, not what part of it they hold: the percentage stands and the total
  // moves to the figure it is a share of. Returns the amount the new total works out at, which
  // is what the input needs to tell its own figure from one set elsewhere — rounding the total
  // to the penny can leave it a fraction off what was typed.
  const setAmount = (index: number, amount: number): number => {
    const percentage = Number(owners[index].ownership_percentage) || 0;
    if (percentage <= 0) return 0;
    const newTotal = Math.round((amount * 10000) / percentage) / 100;
    onTotalChange?.(newTotal);
    return (newTotal * percentage) / 100;
  };

  const shareSum = ownersShareSum(owners);
  const sumMatches = ownersSumTo100(owners);

  const showRows = showJointToggle ? isJoint : true;

  const sumWarning = owners.length > 0 && !sumMatches && (
    <Text size="xs" c="error.6">
      Shares must add up to 100% (currently {shareSum}%).
    </Text>
  );

  const externalMarker = (
    <Text size="sm" c="gray.7" style={{ whiteSpace: 'nowrap' }}>
      (External)
    </Text>
  );

  const ownerName = (owner: OwnerRowValue, index: number) => {
    if (owner.client_id) {
      return (
        <Text size="sm" fw={readOnly ? FONT_WEIGHTS.semibold : FONT_WEIGHTS.medium} c="gray.10">
          {memberName(owner.client_id)}
        </Text>
      );
    }

    // Being read, an owner from outside the household is named and marked in the same breath,
    // so the marker reads as part of who they are rather than as a note on an empty field.
    if (readOnly) {
      return (
        <Group gap={4} wrap="nowrap">
          <Text size="sm" fw={FONT_WEIGHTS.semibold} c="gray.10">
            {owner.external_name || 'Unnamed owner'}
          </Text>
          {externalMarker}
        </Group>
      );
    }

    const nameInput = (
      <TextInput
        placeholder="Owner name"
        variant={isValuation ? 'unstyled' : 'default'}
        disabled={disabled}
        value={owner.external_name ?? ''}
        onChange={event => updateOwner(index, { external_name: event.currentTarget.value })}
      />
    );

    // Only the reworked table says in words that an owner sits outside the household; the
    // classic list leaves the input to fill its column as it always has. The input is allowed
    // to shrink so the marker keeps its place in the column rather than being pushed out of it.
    return isValuation ? (
      <Group gap={4} wrap="nowrap">
        <Box style={{ flex: 1, minWidth: 0 }}>{nameInput}</Box>
        {externalMarker}
      </Group>
    ) : (
      nameInput
    );
  };

  // An external owner's slice is recorded but sits outside the household's valuation, so it
  // reads in grey rather than the brand colour the household's own shares use.
  const shareBar = (owner: OwnerRowValue, percentage: number) => {
    const isExternal = isValuation && isExternalOwner(owner);
    const brandTrack = isValuation ? 'brand-1' : 'gray-1';
    const track = isExternal ? 'gray-2' : brandTrack;
    const fill = isExternal ? 'gray-4' : 'brand-3';
    return (
      <Box
        style={{
          flex: 1,
          height: 8,
          borderRadius: 4,
          background: `var(--mantine-color-${track})`,
        }}
      >
        <Box
          style={{
            height: 8,
            borderRadius: 4,
            width: `${Math.min(100, percentage)}%`,
            background: `var(--mantine-color-${fill})`,
          }}
        />
      </Box>
    );
  };

  // Rows the joint toggle keeps hidden are not worth building on every render.
  const rows =
    showRows &&
    owners.map((owner, index) => {
      const percentage = Number(owner.ownership_percentage) || 0;
      return (
        <Group
          key={owner.id ?? index}
          align="center"
          wrap="nowrap"
          gap="md"
          px={isValuation ? 'md' : undefined}
          py={isValuation ? 'sm' : undefined}
          style={
            isValuation && index > 0
              ? { borderTop: '1px solid var(--mantine-color-gray-2)' }
              : undefined
          }
        >
          <Box style={{ width: nameWidth, ...(isValuation && { flexShrink: 0 }) }}>
            {ownerName(owner, index)}
          </Box>
          {shareBar(owner, percentage)}
          {readOnly ? (
            <ReadOnlyCell width={PERCENTAGE_WIDTH}>{percentage}%</ReadOnlyCell>
          ) : (
            <NumberInput
              w={PERCENTAGE_WIDTH}
              min={0}
              max={100}
              decimalScale={SHARE_DECIMAL_PLACES}
              hideControls={isValuation}
              disabled={disabled}
              suffix="%"
              placeholder={isValuation ? '0%' : undefined}
              aria-label="Ownership share"
              value={owner.ownership_percentage ?? ''}
              onChange={value =>
                updateOwner(index, { ownership_percentage: value === '' ? null : Number(value) })
              }
            />
          )}
          {isValuation && !readOnly ? (
            <OwnerAmountInput
              total={total}
              percentage={percentage}
              // An owner with no share yet is no part of anything, so there is no whole for a
              // figure in money to name. The share is what unlocks the money beside it.
              disabled={disabled || onTotalChange == null || percentage <= 0}
              onCommit={amount => setAmount(index, amount)}
            />
          ) : (
            <ReadOnlyCell
              width={isValuation ? AMOUNT_WIDTH : CLASSIC_AMOUNT_WIDTH}
              muted={!readOnly}
            >
              {formatAmount((total * percentage) / 100)}
            </ReadOnlyCell>
          )}
          {readOnly ? (
            isValuation && <Box style={{ width: REMOVE_WIDTH }} />
          ) : (
            <ActionIcon
              variant="subtle"
              color="gray"
              disabled={disabled}
              onClick={() => removeOwner(index)}
              aria-label="Remove owner"
            >
              {isValuation ? <IconX size={18} /> : <IconTrash size={16} />}
            </ActionIcon>
          )}
        </Group>
      );
    });

  return (
    <Stack gap="sm">
      <Group justify={showJointToggle ? 'space-between' : 'flex-end'} align="center">
        {showJointToggle &&
          (readOnly ? (
            <ViewField fieldLabel="Joint asset" value={isJoint ? 'Yes' : 'No'} />
          ) : (
            <Switch
              checked={isJoint}
              disabled={disabled}
              onChange={event => onJointChange?.(event.currentTarget.checked)}
              label="Joint asset. Enter each owner's share."
            />
          ))}
        {showRows && !readOnly && (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <Button
                variant={isValuation ? 'text' : 'tertiary'}
                size="xs"
                px={isValuation ? undefined : 9}
                aria-label="Add owner"
                disabled={disabled}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  Add owner
                  <IconChevronDown size={16} color="var(--mantine-color-brand-7)" />
                </span>
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {availableMembers.length > 0 && (
                <Fragment>
                  <Menu.Label>Family members</Menu.Label>
                  {availableMembers.map(member => (
                    <Menu.Item key={member.clientId} onClick={() => addOwner(member.clientId)}>
                      {`${member.client.firstName} ${member.client.lastName}`}
                      {member.isMain ? ' (Main)' : ''}
                    </Menu.Item>
                  ))}
                </Fragment>
              )}
              <Menu.Item onClick={() => addOwner(EXTERNAL)}>Add external owner</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>

      {showRows &&
        (isValuation ? (
          <>
            {owners.length > 0 && (
              <Box style={{ border: '1px solid var(--mantine-color-gray-2)', borderRadius: 16 }}>
                <Group align="center" wrap="nowrap" gap="md" px="md" pt="sm">
                  <Box style={{ width: nameWidth, flexShrink: 0 }}>
                    <Text size="xs" c="gray.7">
                      Owner
                    </Text>
                  </Box>
                  <Box style={{ flex: 1 }} />
                  <Text size="xs" c="gray.7" w={PERCENTAGE_WIDTH + AMOUNT_WIDTH + 16}>
                    Valuation
                  </Text>
                  <Box style={{ width: REMOVE_WIDTH }} />
                </Group>
                {rows}
              </Box>
            )}
            {sumWarning}
          </>
        ) : (
          <Stack gap="xs">
            {rows}
            {sumWarning}
          </Stack>
        ))}
    </Stack>
  );
}

type OwnerAmountInputProps = {
  total: number;
  percentage: number;
  disabled: boolean;
  /** Sets the total this amount is a share of, and answers with what it then works out at. */
  onCommit: (amount: number) => number;
};

/**
 * An owner's slice in money, which names the item's total value through their share of it. The
 * total is kept to the penny, so a typed amount only survives the round trip back through the
 * share to within a fraction of one — echoing it straight back would rewrite the figure under
 * the cursor, and the first digit of a large amount would be read as the whole of it and snap
 * the field about. So the total is set on every keystroke while what was typed stays on screen:
 * waiting for a blur would lose the last thing typed when Enter submits the form straight from
 * the field. The field goes back to reading off the total whenever it moves for some other
 * reason — another owner's slice naming it, or this owner's percentage being edited.
 */
function OwnerAmountInput({ total, percentage, disabled, onCommit }: OwnerAmountInputProps) {
  const derived = total > 0 ? (total * percentage) / 100 : '';
  const [typed, setTyped] = useState<string | number | null>(null);
  const [typedStandsFor, setTypedStandsFor] = useState<string | number>(derived);

  // While nothing has been typed the field simply follows the share. Once something has, it
  // holds on to it until the amount the share stands for moves for some other reason.
  if (derived !== typedStandsFor) {
    setTyped(null);
    setTypedStandsFor(derived);
  }

  // Storing the share and remembering what it stands for in the same breath is what keeps the
  // typed figure on screen: the reset above only fires when the two disagree.
  const commit = (value: string | number) => {
    setTyped(value);
    setTypedStandsFor(onCommit(value === '' ? 0 : Number(value)));
  };

  return (
    <NumberInput
      w={AMOUNT_WIDTH}
      min={0}
      hideControls
      prefix="£"
      thousandSeparator=","
      placeholder="£0"
      aria-label="Ownership amount"
      disabled={disabled}
      value={typed ?? derived}
      onChange={commit}
    />
  );
}
